import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import Logo3D from "@/components/Logo3D";
import { supabase } from "@/integrations/supabase/client";
import { useQuery } from "@tanstack/react-query";

const categories = [
  "All", "Interior", "Residential", "Commercial", "Institutional", "Industrial", "Cultural", "Renovation", "Unique"
];

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.05 } },
};

const staggerItem = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35 } },
};

const projectsPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Architecture Portfolio — Dhivisha Architects",
  "description": "Explore 350+ completed architectural projects across residential, commercial, cultural, industrial, and interior categories by COA-registered architects in Kumbakonam, Cauvery Delta region.",
  "mainEntity": { "@id": "https://dhivishaarchitects.com/#organization" },
  "speakable": {
    "@type": "SpeakableSpecification",
    "cssSelector": ["h1", ".projects-intro"],
  },
};

const Projects = () => {
  const [active, setActive] = useState("All");

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ["public-projects"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("projects")
        .select("slug, title, category, thumbnail_url, description")
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const filtered = active === "All" ? projects : projects.filter((p) => p.category === active);

  return (
    <Layout transparentNav>
      <SEOHead
        title="Projects — Registered Architect Portfolio in Kumbakonam & Delta Region"
        description="Explore 350+ completed projects by Dhivisha Architects, COA-registered firm in Kumbakonam: residential homes, commercial buildings, interiors, cultural centers & renovations across the Cauvery Delta districts."
        path="/projects"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(projectsPageJsonLd) }}
      />
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1486718448742-163732cd1544?w=1200&q=70" alt="Project showcase" className="w-full h-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
          <div className="absolute inset-0 bg-secondary/70" />
        </div>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 text-center px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-primary-foreground mb-4">Our Projects</h1>
          <p className="text-lg text-primary-foreground/80 max-w-2xl mx-auto">
            A holistic understanding of our expertise in each area — our portfolio structured into multiple categories.
          </p>
        </motion.div>
      </section>

      <div className="py-8 bg-muted flex justify-center">
        <Logo3D size="md" animate className="mx-auto" />
      </div>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex flex-wrap justify-center gap-2 mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActive(cat)}
                className={`px-5 py-2 text-xs font-semibold uppercase tracking-wider rounded transition-all active:scale-95 ${
                  active === cat ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="text-center py-20 text-muted-foreground">Loading projects…</div>
          ) : (
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                variants={staggerContainer}
                initial="hidden"
                animate="visible"
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
              >
                {filtered.map((project) => (
                  <Link key={project.slug} to={`/projects/${project.slug}`}>
                    <motion.div
                      variants={staggerItem}
                      layout
                      whileHover={{ y: -4 }}
                      whileTap={{ scale: 0.98 }}
                      className="group relative overflow-hidden rounded bg-card border border-border hover:border-primary transition-colors cursor-pointer"
                    >
                      <div className="h-64 overflow-hidden">
                        {project.thumbnail_url ? (
                          <img src={project.thumbnail_url} alt={project.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" decoding="async" />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground text-sm">No image</div>
                        )}
                      </div>
                      <div className="p-5">
                        <span className="text-xs uppercase tracking-widest text-primary font-semibold">{project.category}</span>
                        <h3 className="text-lg font-bold text-foreground mt-1">{project.title}</h3>
                        <p className="text-sm text-muted-foreground mt-2">{project.description}</p>
                      </div>
                    </motion.div>
                  </Link>
                ))}
              </motion.div>
            </AnimatePresence>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default Projects;
